import { flatMap, partial, size } from "lodash";
import { useDebounce } from "@lkf/hooks";
import { useInfiniteQuery } from "@tanstack/react-query";
import { SearchableWorkspace } from "@radarly/models/workspace";
import { useWorkspaceRepository } from "./useWorkspaceRepository";

function isNotTheLastPage<T>(currentPage: T[], limit: number) {
  return size(currentPage) >= limit;
}

function getNextPage<T>(allPages: T[][], limit: number) {
  return size(allPages) * limit;
}

function getNextPageParamFromLimit<T>(
  limit: number,
  currentPage: T[],
  allPages: T[][]
) {
  if (isNotTheLastPage(currentPage, limit)) {
    return getNextPage(allPages, limit);
  }
  return undefined;
}

export function useSearchWorkspaces(search: string, limit: number = 15) {
  const repository = useWorkspaceRepository();
  const debouncedSearch = useDebounce(search, 500);
  const { data, isFetchingNextPage, fetchNextPage, hasNextPage } =
    useInfiniteQuery(
      ["/workspaces/search", debouncedSearch],
      ({ pageParam = 0 }) => {
        return repository.searchWorkspaces(debouncedSearch, {
          start: pageParam,
          limit,
        });
      },
      {
        getNextPageParam: partial(getNextPageParamFromLimit, limit),
      }
    );
  return {
    workspaces: data ? flatMap(data.pages) : ([] as SearchableWorkspace[]),
    loading: !!isFetchingNextPage,
    fetchMore: fetchNextPage,
    canFetchMore: hasNextPage,
  };
}
